import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  token: null
};

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'INITIALIZE':
      return { 
        ...state, 
        isAuthenticated: !!action.payload.token,
        token: action.payload.token,
        user: action.payload.user || null
      };
    case 'LOGIN_REQUEST':
      return { ...state, isLoading: true, error: null };
    case 'LOGIN_SUCCESS':
      return { 
        ...state, 
        isLoading: false, 
        isAuthenticated: true, 
        user: action.payload.user,
        token: action.payload.token,
        error: null
      };
    case 'LOGIN_FAILURE':
      return { 
        ...state, 
        isLoading: false, 
        isAuthenticated: false, 
        error: action.payload.error 
      };
    case 'LOGOUT':
      return { 
        ...state, 
        isAuthenticated: false, 
        user: null,
        token: null,
        error: null
      };
    default:
      return state;
  }
};

const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      dispatch({ 
        type: 'INITIALIZE', 
        payload: { token } 
      });
    }
  }, []);

  const login = useCallback(async (username, password) => {
    dispatch({ type: 'LOGIN_REQUEST' });
    
    try {
      const response = await fetch('https://mpp1_moomark_plantmonitoring.smartmoo.com/auth/test/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      localStorage.setItem('authToken', data.token);
      
      dispatch({ 
        type: 'LOGIN_SUCCESS', 
        payload: { user: data.user, token: data.token } 
      });

      return { success: true };
    } catch (error) {
      dispatch({ 
        type: 'LOGIN_FAILURE', 
        payload: { error: error.message } 
      });
      return { success: false, error: error.message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    dispatch({ type: 'LOGOUT' });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthProvider, useAuth };
